import './CarrierRatingsPanel.less';

import { Button, Col, Form, Row, Select, Slider } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useCarrierUpdate } from '../../hooks/apiHooks';
import { useCarrierRating, useRatingUpdate } from '../../hooks/apiHooks/rating';
import { CarrierTier } from '../../models/Rating';
import { UserRole } from '../../models/User';
import PermissionsToggle from '../common/PermissionsToggle';

/*
interface IProps {
  id: string;
  carrier: Carrier;
  userRole?: UserRole;
  onHeaderActions?: (actions: ReactNode) => void;
}
*/

const marks = {
  0: '0',
  1: '1',
  2: '2',
  3: '3',
  4: '4',
  5: '5',
};

const CarrierRatingsPanel = ({ carrier, id, onHeaderActions, userRole }) => {
  const { rating } = useCarrierRating(carrier.id);
  const updateMutation = useRatingUpdate();
  const carrierUpdateMutation = useCarrierUpdate();
  const carrierRef = useRef(carrier);
  carrierRef.current = carrier;

  const [form] = Form.useForm();

  const onFinish = useCallback(
    (values) => {
      values.carrierId = carrier.id;
      if (rating?.id) {
        values.id = rating.id;
      }
      updateMutation.mutate(values);
    },
    [carrier.id, rating?.id, updateMutation]
  );

  const onTogglePermission = useCallback(
    (permission) => {
      const newCarrier = {
        ...carrierRef.current,
        ratingPermission: permission,
      };
      carrierUpdateMutation.mutate(newCarrier);
    },
    [carrierUpdateMutation]
  );

  useEffect(() => {
    if (rating) {
      form.setFieldsValue(rating);
    } else {
      form.setFieldsValue({
        carrierTier: CarrierTier.PREFERRED,
        cost: 0,
        damageClaims: 0,
        honesty: 0,
        pastPerformance: 0,
        reliability: 0,
      });
    }
  }, [form, rating]);

  const headerActions = useMemo(
    () =>
      userRole === UserRole.SUPER_ADMIN ? (
        <PermissionsToggle
          adminOnly
          defaultValue={carrier.ratingPermission}
          submitting={carrierUpdateMutation.isLoading}
          onChange={onTogglePermission}
        />
      ) : null,
    [
      userRole,
      carrier.ratingPermission,
      carrierUpdateMutation.isLoading,
      onTogglePermission,
    ]
  );

  const headerActionsRef = useRef(headerActions);
  headerActionsRef.current = headerActions;

  useEffect(() => {
    if (onHeaderActions) {
      onHeaderActions(headerActionsRef.current);
    }
  }, [
    onHeaderActions,
    userRole,
    carrier.ratingPermission,
    carrierUpdateMutation.isLoading,
  ]);

  return (
    <div id={id}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={24}>
          <Col span={12} xl={8}>
            <Form.Item
              className="carrier-ratings-panel__form-item"
              label="Damage Claims"
              name="damageClaims"
              style={{ maxWidth: 400 }}
            >
              <Slider marks={marks} max={5} min={0} />
            </Form.Item>

            <Form.Item
              className="carrier-ratings-panel__form-item"
              label="Past Performance"
              name="pastPerformance"
            >
              <Slider marks={marks} max={5} min={0} />
            </Form.Item>

            <Form.Item
              className="carrier-ratings-panel__form-item"
              label="Honesty"
              name="honesty"
            >
              <Slider marks={marks} max={5} min={0} />
            </Form.Item>
          </Col>

          <Col span={12} xl={8}>
            <Form.Item
              className="carrier-ratings-panel__form-item"
              label="Reliability"
              name="reliability"
            >
              <Slider marks={marks} max={5} min={0} />
            </Form.Item>

            <Form.Item
              className="carrier-ratings-panel__form-item"
              label="Cost"
              name="cost"
            >
              <Slider marks={marks} max={5} min={0} />
            </Form.Item>

            <Form.Item
              className="carrier-ratings-panel__form-item"
              label="Carrier Tier"
              name="carrierTier"
            >
              <Select>
                <Select.Option value={CarrierTier.PREFERRED}>
                  Preferred
                </Select.Option>
                <Select.Option value={CarrierTier.SECOND}>
                  2nd Class
                </Select.Option>
                <Select.Option value={CarrierTier.THIRD}>
                  3rd Class
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Button
            htmlType="submit"
            loading={updateMutation.isLoading}
            type="primary"
          >
            SAVE
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CarrierRatingsPanel;
